import React, { useState, useEffect } from 'react'
import SpaceTheme from '../components/SpaceTheme'
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  TextField,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Check as CheckIcon,
  Star as StarIcon,
  Edit as EditIcon
} from '@mui/icons-material'
import { useAuth } from '../hooks/useAuth'
import { db } from '../firebase/config'
import { doc, getDoc, addDoc, collection } from 'firebase/firestore'
import toast from 'react-hot-toast'

const WordMeaningQuiz = ({ taskId, taskTitle, onBack }) => {
  const { user } = useAuth()
  const [words, setWords] = useState([])
  const [shuffledWords, setShuffledWords] = useState([])
  const [currentWordIndex, setCurrentWordIndex] = useState(0)
  const [currentWord, setCurrentWord] = useState(null)
  const [userAnswer, setUserAnswer] = useState('')
  const [correctAnswers, setCorrectAnswers] = useState(0)
  const [totalQuestions, setTotalQuestions] = useState(0)
  const [loading, setLoading] = useState(true)
  const [quizStarted, setQuizStarted] = useState(false)
  const [showAnswerDialog, setShowAnswerDialog] = useState(false)
  const [answerFeedback, setAnswerFeedback] = useState({ isCorrect: false, correctAnswer: '', userAnswer: '' })
  const [showResultDialog, setShowResultDialog] = useState(false)
  const [quizComplete, setQuizComplete] = useState(false)

  useEffect(() => {
    if (taskId) {
      loadWordTask()
    }
  }, [taskId])

  const loadWordTask = async () => {
    try {
      setLoading(true)
      
      const taskDoc = await getDoc(doc(db, 'wordTasks', taskId))
      
      if (taskDoc.exists()) {
        const data = taskDoc.data()
        const wordsData = data.words || []
        
        if (wordsData.length > 0) {
          setWords(wordsData)
          startQuiz(wordsData)
        } else {
          toast.error('Bu görevde kelime bulunamadı')
          onBack()
        }
      } else {
        toast.error('Görev bulunamadı')
        onBack()
      }
    } catch (error) {
      console.error('Error loading word task:', error)
      toast.error('Kelime görevi yüklenirken hata oluştu')
      onBack()
    } finally {
      setLoading(false)
    }
  }

  const startQuiz = (wordsData) => {
    // Android uygulamasıyla aynı mantık
    setTotalQuestions(wordsData.length)
    const shuffled = [...wordsData].sort(() => Math.random() - 0.5)
    setShuffledWords(shuffled)
    setCurrentWordIndex(0)
    setCorrectAnswers(0)
    setQuizStarted(true)
    setQuizComplete(false)
    showNextQuestion(0, shuffled)
  }

  const showNextQuestion = (questionIndex, shuffledWordsArray) => {
    if (questionIndex >= shuffledWordsArray.length) {
      finishQuiz()
      return
    }

    const word = shuffledWordsArray[questionIndex]
    setCurrentWord(word)
    setUserAnswer('')
  }

  const isCorrectAnswer = (userAnswer, correctAnswer) => {
    // Basit anlam kontrolü (Android uygulamasındaki MeaningChecker benzeri)
    const userLower = userAnswer.toLowerCase().trim()
    const correctLower = correctAnswer.toLowerCase().trim()
    
    // Tam eşleşme
    if (userLower === correctLower) return true
    
    // Noktalama işaretlerini temizle
    const cleanUser = userLower.replace(/[.,!?;:()]/g, '').trim()
    const cleanCorrect = correctLower.replace(/[.,!?;:()]/g, '').trim()
    
    if (cleanUser === cleanCorrect) return true
    
    // Ana kelimeler kontrolü (virgül ile ayrılmış)
    const correctParts = cleanCorrect.split(',').map(part => part.trim())
    const userParts = cleanUser.split(',').map(part => part.trim())
    
    // Herhangi bir kısmı doğruysa kabul et
    for (const userPart of userParts) {
      for (const correctPart of correctParts) {
        if (userPart === correctPart) return true
        // Kelime içinde geçiyorsa da kabul et
        if (correctPart.includes(userPart) || userPart.includes(correctPart)) {
          if (userPart.length >= 3 && correctPart.length >= 3) return true
        }
      }
    }
    
    return false
  }

  const handleSubmitAnswer = () => {
    if (!userAnswer.trim()) {
      toast.warning('Lütfen bir cevap yazın')
      return
    }

    // Doğru cevabı kontrol et
    const correctMeaning = currentWord.turkish || currentWord.meaning
    const isCorrect = isCorrectAnswer(userAnswer, correctMeaning)
    
    if (isCorrect) {
      setCorrectAnswers(correctAnswers + 1)
    }
    
    setAnswerFeedback({
      isCorrect,
      correctAnswer: correctMeaning,
      userAnswer: userAnswer
    })
    setShowAnswerDialog(true)
  }

  const handleAnswerDialogClose = () => {
    setShowAnswerDialog(false)
    
    if (currentWordIndex >= totalQuestions - 1) {
      // Quiz tamamlandı
      finishQuiz()
    } else {
      // Sonraki soruya geç
      const nextIndex = currentWordIndex + 1
      setCurrentWordIndex(nextIndex)
      showNextQuestion(nextIndex, shuffledWords)
    }
  }

  const finishQuiz = () => {
    setQuizComplete(true)
    setShowResultDialog(true)
    
    // Sonucu Firestore'a kaydet (Android uygulamasıyla aynı)
    saveQuizResult()
  }

  const saveQuizResult = async () => {
    try {
      const studentId = user.uid
      
      // Önce word task'ın teacherId'sini al (Android uygulamasıyla aynı)
      const taskDoc = await getDoc(doc(db, 'wordTasks', taskId))
      
      if (taskDoc.exists()) {
        const taskData = taskDoc.data()
        const teacherId = taskData.teacherId
        const classId = taskData.classId
        const className = taskData.className
        
        const result = {
          wordTaskId: taskId,
          studentId: studentId,
          teacherId: teacherId,
          classId: classId,
          className: className,
          score: correctAnswers,
          totalQuestions: totalQuestions,
          completionDate: new Date(),
          taskMode: 'meaning'
        }
        
        await addDoc(collection(db, 'wordTaskResults'), result)
        console.log('Anlam sorma quiz sonucu kaydedildi:', result)
      }
    } catch (error) {
      console.error('Error saving quiz result:', error)
      toast.error('Sonuç kaydedilirken hata oluştu')
    }
  }

  const handleQuizComplete = () => {
    setShowResultDialog(false)
    toast.success('Anlam sorma testi başarıyla tamamlandı!')
    onBack()
  }

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSubmitAnswer()
    }
  }

  const getScoreColor = () => {
    const percentage = (correctAnswers / totalQuestions) * 100
    if (percentage >= 80) return 'success.main'
    if (percentage >= 60) return 'warning.main'
    return 'error.main'
  }

  const getScoreEmoji = () => {
    const percentage = (correctAnswers / totalQuestions) * 100
    if (percentage >= 90) return '🎉'
    if (percentage >= 80) return '😊'
    if (percentage >= 60) return '😐'
    return '😔'
  }

  if (loading) {
    return (
      <SpaceTheme>
        <Box sx={{ flexGrow: 1 }}>
          <AppBar position="static" sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
          }}>
            <Toolbar>
              <IconButton edge="start" color="inherit" onClick={onBack} sx={{ mr: 2 }}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                {taskTitle || 'Anlam Sorma'}
              </Typography>
            </Toolbar>
          </AppBar>
          <Container maxWidth="md" sx={{ py: 3 }}>
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <LinearProgress sx={{ mb: 2 }} />
              <Typography>Anlam sorma testi yükleniyor...</Typography>
            </Box>
          </Container>
        </Box>
      </SpaceTheme>
    )
  }

  if (!quizStarted || words.length === 0) {
    return (
      <SpaceTheme>
        <Box sx={{ flexGrow: 1 }}>
          <AppBar position="static" sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
          }}>
            <Toolbar>
              <IconButton edge="start" color="inherit" onClick={onBack} sx={{ mr: 2 }}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                Test Hatası
              </Typography>
            </Toolbar>
          </AppBar>
          <Container maxWidth="md" sx={{ py: 3 }}>
            <Alert severity="error">
              Anlam sorma testi başlatılamadı. Lütfen daha sonra tekrar deneyin.
            </Alert>
          </Container>
        </Box>
      </SpaceTheme>
    )
  }

  const progress = ((currentWordIndex + 1) / totalQuestions) * 100

  return (
    <SpaceTheme>
      <Box sx={{ flexGrow: 1 }}>
        {/* App Bar */}
        <AppBar position="static" sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
        }}>
          <Toolbar>
            <IconButton edge="start" color="inherit" onClick={onBack} sx={{ mr: 2 }}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              {taskTitle || 'Anlam Sorma'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <StarIcon sx={{ color: '#FFD700' }} />
              <Typography variant="body2">
                {correctAnswers}/{currentWordIndex}
              </Typography>
            </Box>
          </Toolbar>
        </AppBar>

        {/* Main Content */}
        <Container maxWidth="md" sx={{ py: 3 }}>
          {/* Progress Card */}
          <Card sx={{ 
            mb: 3,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
          }}>
            <CardContent sx={{ pb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Soru {currentWordIndex + 1} / {totalQuestions}
                </Typography>
                <Chip 
                  icon={<StarIcon />} 
                  label={`${correctAnswers} Doğru`} 
                  color="primary" 
                  size="small" 
                />
              </Box>
              <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 4 }} />
            </CardContent>
          </Card>

          {/* Question Card */}
          <Card sx={{ 
            mb: 3,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
          }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
                📝 Bu kelimenin anlamını yazın:
              </Typography>
              <Typography variant="h3" fontWeight="bold" color="text.primary" sx={{ 
                textAlign: 'center', 
                my: 4, 
                p: 3,
                backgroundColor: 'rgba(103, 110, 234, 0.1)',
                borderRadius: 3,
                border: '2px solid rgba(103, 110, 234, 0.3)'
              }}>
                {currentWord?.english || currentWord?.word || ''}
              </Typography>

              <TextField
                fullWidth
                variant="outlined"
                label="Cevabınızı yazın..."
                value={userAnswer}
                onChange={(e) => setUserAnswer(e.target.value)}
                onKeyPress={handleKeyPress}
                sx={{ 
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    fontSize: '1.2rem',
                    '& fieldset': {
                      borderColor: 'rgba(103, 110, 234, 0.3)',
                      borderWidth: 2
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(103, 110, 234, 0.5)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'primary.main',
                    }
                  }
                }}
                InputProps={{
                  startAdornment: <EditIcon sx={{ mr: 1, color: 'primary.main' }} />
                }}
              />

              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleSubmitAnswer}
                  disabled={!userAnswer.trim()}
                  startIcon={<CheckIcon />}
                  sx={{ minWidth: 200 }}
                >
                  Cevabı Kontrol Et
                </Button>
              </Box>

              <Alert severity="info" sx={{ mt: 3 }}>
                💡 <strong>İpucu:</strong> Cevabınızı Türkçe yazın. Virgülle ayrılmış birden fazla anlam yazabilirsiniz.
              </Alert>
            </CardContent>
          </Card>
        </Container>

        {/* Answer Feedback Dialog */}
        <Dialog open={showAnswerDialog} onClose={handleAnswerDialogClose}>
          <DialogTitle sx={{ textAlign: 'center' }}>
            {answerFeedback.isCorrect ? '✅ Doğru!' : '❌ Yanlış!'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>Sizin cevabınız:</strong> {answerFeedback.userAnswer}
              </Typography>
              <Typography variant="body1">
                <strong>Doğru cevap:</strong> {answerFeedback.correctAnswer}
              </Typography>
              {answerFeedback.isCorrect && (
                <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                  Tebrikler! 🎉
                </Typography>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center' }}>
            <Button onClick={handleAnswerDialogClose} variant="contained">
              {currentWordIndex >= totalQuestions - 1 ? 'Sonuçları Gör' : 'Devam Et'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Final Result Dialog */}
        <Dialog open={showResultDialog} onClose={handleQuizComplete}>
          <DialogTitle sx={{ textAlign: 'center' }}>
            {getScoreEmoji()} Test Tamamlandı!
          </DialogTitle>
          <DialogContent sx={{ textAlign: 'center' }}>
            <Typography variant="h4" color={getScoreColor()} sx={{ mb: 2 }}>
              {correctAnswers}/{totalQuestions}
            </Typography>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Doğru cevap oranı: %{Math.round((correctAnswers / totalQuestions) * 100)}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {correctAnswers === totalQuestions 
                ? 'Mükemmel! Tüm soruları doğru cevapladınız!' 
                : correctAnswers >= totalQuestions * 0.8 
                ? 'Harika! Çok başarılı bir performans!' 
                : correctAnswers >= totalQuestions * 0.6 
                ? 'İyi! Biraz daha çalışarak daha da iyi olabilirsiniz.'
                : 'Bu kelimeleri daha fazla çalışmanız gerekiyor.'
              }
            </Typography>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center' }}>
            <Button onClick={handleQuizComplete} variant="contained" size="large">
              Tamam
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </SpaceTheme>
  )
}

export default WordMeaningQuiz
